/**
 * Les questions qui ne se posent pas toujours.
 *
 * UNE QUESTION PEUT DÉPENDRE D'UNE AUTRE : « Quels ARI seront utilisés ? » n'a
 * de sens que si le client vient de répondre qu'il en fournissait. La règle est
 * stockée dans `condition`, au MÊME format que les conseils conditionnels —
 * `{ combinaison, requises, exclues }` sur les libellés de réponses — augmenté
 * du `questionId` dont elle dépend. Un seul format, donc un seul évaluateur :
 * `regleRemplie`, déjà écrit et déjà éprouvé.
 *
 * L'ARBRE N'EST NULLE PART DÉCLARÉ. Une question conditionnelle peut désigner
 * une question elle-même conditionnelle : c'est ce chaînage, et lui seul, qui
 * fait la profondeur. Aucune règle n'a donc à connaître son propre niveau, et
 * rien à migrer le jour où quelqu'un en ajoute un.
 *
 * DEUX PARTIS PRIS, ET ILS VONT DANS LE MÊME SENS — NE JAMAIS TAIRE UNE
 * QUESTION PAR ACCIDENT :
 *  - une règle qui désigne une question DISPARUE est ignorée, et la question se
 *    pose. L'inverse la ferait taire pour toujours, sans que personne ne voie
 *    jamais pourquoi ;
 *  - un cycle rend la question invisible, mais ne boucle pas. Le service refuse
 *    déjà d'en écrire un ; ce garde-fou existe pour les données antérieures et
 *    pour ce qui entrerait par une autre porte.
 *
 * Fonctions pures, sans dépendance à Vue. LE BACKEND NE LES APPELLE PAS : le
 * filtrage vit côté saisie, et les réponses aux questions masquées sont
 * effacées à la source (`reponsesDevenuesSansObjet`). Le PDF n’imprime donc
 * rien de faux — il ne rend que les questions répondues. Le jour où une
 * écriture pourra venir d’ailleurs que de ces écrans, c’est ici qu’il faudra
 * brancher un contrôle serveur.
 */
import { regleRemplie, reponsesRetenues } from './conseilsQuestions.js'

/**
 * La question est-elle posée, compte tenu de ce qui a déjà été répondu ?
 *
 * @param {object} question la question examinée
 * @param {object[]} questions toutes les questions du questionnaire
 * @param {object} reponses réponses indexées par identifiant de question
 * @returns {boolean}
 */
export function questionEstVisible(question, questions, reponses) {
  return visible(question, indexer(questions), reponses || {}, new Set())
}

/**
 * Les questions réellement posées, dans l'ordre reçu.
 *
 * C'est CETTE liste que les écrans de saisie parcourent, et non la liste
 * complète : une question masquée ne doit ni s'afficher, ni compter dans les
 * champs obligatoires, ni s'imprimer au dossier.
 */
export function questionsVisibles(questions, reponses) {
  const liste = Array.isArray(questions) ? questions : []
  const parId = indexer(liste)
  const table = reponses || {}
  return liste.filter((question) => visible(question, parId, table, new Set()))
}

/**
 * Les identifiants des questions qui ne sont PLUS posées mais qui portent
 * encore une réponse.
 *
 * Le client répond à une sous-question, puis change la réponse qui l'avait fait
 * apparaître : sa saisie n'a plus d'objet. La laisser en base ferait porter au
 * dossier — et au PDF envoyé au formateur — une réponse à une question que
 * personne n'a vue. On l'efface donc à la source, une bonne fois, plutôt que de
 * demander à chaque lecture de se souvenir de la filtrer.
 */
export function reponsesDevenuesSansObjet(questions, reponses) {
  const liste = Array.isArray(questions) ? questions : []
  const parId = indexer(liste)
  const table = reponses || {}

  return liste
    .filter((question) => !visible(question, parId, table, new Set()))
    .map((question) => question.id)
    .filter((id) => aUneReponse(table[id]))
}

/** Y a-t-il quelque chose à effacer derrière cette question ? */
function aUneReponse(reponse) {
  if (!reponse) return false
  const texte = reponse.answer
  if (texte !== null && texte !== undefined && String(texte).trim() !== '') return true
  if (Array.isArray(reponse.selectedOptions) && reponse.selectedOptions.length) return true
  // Le commentaire du commercial compte : il a été écrit sur une question qui
  // n'est plus posée, il n'a pas plus d'objet que la réponse elle-même.
  return Boolean(reponse.commentaire && String(reponse.commentaire).trim())
}

function indexer(questions) {
  return new Map((Array.isArray(questions) ? questions : []).map((q) => [q.id, q]))
}

function visible(question, parId, reponses, vus) {
  const condition = question?.condition
  if (!condition?.questionId) return true

  // Cycle : on ne pose pas la question, mais on ne tourne pas non plus.
  if (vus.has(question.id)) return false
  vus.add(question.id)

  const parent = parId.get(Number(condition.questionId))
  // Question parente supprimée : la règle n'a plus de sens, la question se pose.
  if (!parent) return true

  // Une sous-question d'une question masquée l'est aussi : c'est ce qui fait
  // que replier une branche replie tout ce qui pend dessous.
  if (!visible(parent, parId, reponses, vus)) return false

  return regleRemplie(condition, reponsesRetenues(reponses[parent.id], parent))
}

/**
 * La règle relue en français, pour l'écran d'administration : « Posée si, à
 * « Fournissez-vous les ARI ? », le client répond « Oui » ».
 *
 * Elle existe pour la même raison que `decrireRegle` à côté : c'est cette
 * phrase qu'on vérifie d'un coup d'œil, pas les cases du formulaire.
 */
export function decrireCondition(condition, questionParente) {
  if (!condition?.questionId) return 'Cette question est toujours posée.'

  const nom = questionParente?.label || 'une autre question'
  const citer = (labels) => labels.map((label) => `« ${label} »`).join(', ')
  const requises = condition.requises || []
  const exclues = condition.exclues || []
  const morceaux = []

  if (requises.length === 1) morceaux.push(`répond ${citer(requises)}`)
  else if (requises.length > 1) {
    morceaux.push(condition.combinaison === 'auMoinsUne'
      ? `répond au moins l’une de ${citer(requises)}`
      : `répond ${citer(requises)} à la fois`)
  }

  if (exclues.length) {
    morceaux.push(exclues.length === 1
      ? `ne répond pas ${citer(exclues)}`
      : `ne répond aucune de ${citer(exclues)}`)
  }

  if (!morceaux.length) return 'Cette question est toujours posée.'
  return `Posée si, à « ${nom} », le client ${morceaux.join(' et ')}.`
}
